import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, Selection } from '@tiptap/pm/state';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import { store } from '@/state/store';

// ═══════════════════════════════════════════════════════════════
// Sledování změn (Fáze 3) — zachytávání úprav při zapnutém režimu.
//
// PRINCIP:
//   • vložený text → dostane mark `insertion` (autor + čas)
//   • smazaný text → se NESMAŽE; místo toho dostane mark `deletion`
//     a zůstane v dokumentu přeškrtnutý. Teprve „Přijmout" ho odstraní.
//
// Mazání zachytáváme na úrovni TRANSAKCÍ, ne kláves. Klávesy nestačí:
// při přepsání označeného textu (uživatel označí text a začne psát)
// ProseMirror text nahradí přímo a žádný Backspace se nekoná — smazání
// původního textu by se ztratilo a nešlo by ho odmítnout.
//
// Postup: ve filterTransaction poznáme mazání, transakci zrušíme a místo
// ní pošleme vlastní, která text jen označí (a případné vložení zachová).
//
// VÝJIMKY (mazání proběhne normálně, bez značky):
//   • vlastní čerstvě vložený text téhož autora — Word to dělá stejně,
//     jinak by vznikaly nesmyslné dvojice ins+del nad stejným úsekem
//   • undo/redo a nahrání obsahu dokumentu
// ═══════════════════════════════════════════════════════════════

export const trackChangesKey = new PluginKey('trackChanges');

/**
 * Meta, kterým označujeme transakce nahrávající obsah dokumentu (otevření
 * souboru, nový dokument). Sledování je musí ignorovat — jinak by se celý
 * načtený dokument označil jako vložený text.
 */
export const LOAD_CONTENT_META = 'trackChangesLoadContent';

function isTracking(): boolean {
  return !!store.get().doc.trackChanges;
}

function currentAuthor(): string {
  const user = store.get().user;
  return user?.name || user?.email || 'Neznámý autor';
}

/** Transakce, které sledování ignoruje. */
function shouldSkip(tr: Transaction): boolean {
  if (tr.getMeta('history$')) return true; // undo/redo
  if (tr.getMeta(trackChangesKey)) return true; // naše vlastní úprava
  if (tr.getMeta(LOAD_CONTENT_META)) return true; // načtení dokumentu
  if (tr.getMeta('preventUpdate')) return true;
  return false;
}

/**
 * Pozná transakci, která nahrazuje celý obsah dokumentu (setContent).
 * Pojistka pro případ, že chybí LOAD_CONTENT_META.
 */
function isContentLoad(tr: Transaction, oldState: EditorState): boolean {
  if (tr.steps.length !== 1) return false;
  const step: any = tr.steps[0];
  if (typeof step.from !== 'number' || typeof step.to !== 'number') return false;
  return step.from <= 1 && step.to >= oldState.doc.content.size - 1;
}

interface DeletedRange {
  from: number;
  to: number;
}

/** Rozsahy (ve STARÉM dokumentu), které transakce maže. */
function collectDeletedRanges(tr: Transaction, oldState: EditorState): DeletedRange[] {
  const out: DeletedRange[] = [];
  for (const step of tr.steps) {
    const s: any = step;
    // KLÍČOVÉ: mazat může jen krok, který obsah NAHRAZUJE (ReplaceStep,
    // ReplaceAroundStep). AddMarkStep/RemoveMarkStep mají také `from`/`to`,
    // ale text nemažou — jen ho označují (např. komentářem nebo tučným
    // písmem). Kdybychom je nerozlišili, každé označení textu by se
    // zaznamenalo jako smazání a text by se tvářil jako odstraněný.
    //
    // Rozlišujeme podle STRUKTURY, ne podle názvu třídy — minifikace názvy
    // tříd přejmenuje a kontrola by v produkčním buildu přestala fungovat.
    // Krok s `mark` je markový; krok se `slice` je nahrazovací.
    if (s.mark !== undefined) continue; // AddMarkStep / RemoveMarkStep
    if (s.slice === undefined) continue; // není nahrazovací krok

    if (typeof s.from !== 'number' || typeof s.to !== 'number') continue;
    if (s.to <= s.from) continue; // nic se nemaže
    // Maže se jen tehdy, když ve starém dokumentu byl v rozsahu text.
    const text = oldState.doc.textBetween(s.from, s.to, '', '');
    if (text) out.push({ from: s.from, to: s.to });
  }
  return out;
}

/** Text, který se má nově vložit (u přepsání). */
function collectInsertedText(tr: Transaction): string {
  let text = '';
  for (const step of tr.steps) {
    const s: any = step;
    if (s.slice?.content) {
      text += s.slice.content.textBetween(0, s.slice.content.size, '', '');
    }
  }
  return text;
}

export const TrackChanges = Extension.create({
  name: 'trackChanges',

  addProseMirrorPlugins() {
    // Referenci na view potřebujeme, abychom mohli poslat náhradní transakci.
    let viewRef: EditorView | null = null;

    return [
      new Plugin({
        key: trackChangesKey,

        view(v) {
          viewRef = v;
          return { destroy: () => { viewRef = null; } };
        },

        // Vložený text označíme až po transakci (nemusíme ji rušit).
        appendTransaction: (trs, oldState, newState) => {
          if (!isTracking()) return null;

          const relevant = trs.filter(
            (tr) => tr.docChanged && !shouldSkip(tr) && !isContentLoad(tr, oldState),
          );
          if (!relevant.length) return null;

          const author = currentAuthor();
          const date = new Date().toISOString();
          const insType = newState.schema.marks.insertion;
          const delType = newState.schema.marks.deletion;
          if (!insType) return null;

          const tr = newState.tr;
          let changed = false;

          for (const original of relevant) {
            for (const step of original.steps) {
              step.getMap().forEach((_os, _oe, newStart, newEnd) => {
                if (newEnd <= newStart) return;
                newState.doc.nodesBetween(newStart, newEnd, (node, pos) => {
                  if (!node.isText) return;
                  const from = Math.max(pos, newStart);
                  const to = Math.min(pos + node.nodeSize, newEnd);
                  if (to <= from) return;
                  // Už označené jako vložené → nepřidávej znovu.
                  if (node.marks.some((m) => m.type === insType)) return;
                  // Text označený jako smazaný nemá být zároveň vložený.
                  if (delType && node.marks.some((m) => m.type === delType)) return;
                  tr.addMark(from, to, insType.create({ author, date }));
                  changed = true;
                });
              });
            }
          }

          if (!changed) return null;
          tr.setMeta(trackChangesKey, true);
          tr.setMeta('addToHistory', false);
          return tr;
        },

        // Mazání musíme zachytit DŘÍV, než proběhne — jinak text zmizí.
        filterTransaction: (tr, state) => {
          if (!isTracking()) return true;
          if (shouldSkip(tr)) return true;
          if (!tr.docChanged) return true;
          if (isContentLoad(tr, state)) return true;

          const deletions = collectDeletedRanges(tr, state);
          if (!deletions.length) return true; // nic se nemaže → pusť dál

          const author = currentAuthor();
          const delType = state.schema.marks.deletion;
          const insType = state.schema.marks.insertion;
          if (!delType) return true;

          // Maže se jen vlastní čerstvě vložený text? Pak ať mazání proběhne
          // normálně (Word to dělá stejně).
          const allOwnInsertions = deletions.every((r) => {
            let onlyOwn = true;
            state.doc.nodesBetween(r.from, r.to, (node) => {
              if (!node.isText) return;
              const own = node.marks.some(
                (m) => m.type === insType && m.attrs.author === author,
              );
              if (!own) onlyOwn = false;
            });
            return onlyOwn;
          });
          if (allOwnInsertions) return true;

          // Jinak transakci zrušíme a nahradíme vlastní: text neodstraníme,
          // jen ho označíme jako smazaný + doplníme případný nový text.
          const insertedText = collectInsertedText(tr);
          const view = viewRef;
          if (!view) return true;

          queueMicrotask(() => {
            try {
              applyTrackedDelete(view, deletions, insertedText, author);
            } catch {
              // Když se náhradní operace nepovede, radši nic — dokument
              // zůstane nedotčený a uživatel může akci zopakovat.
            }
          });
          return false;
        },
      }),
    ];
  },
});

/**
 * Označí mazané rozsahy jako `deletion` a za ně vloží případný nový text
 * (u přepsání). Pracuje od konce, aby se pozice neposouvaly.
 */
function applyTrackedDelete(
  view: EditorView,
  deletions: DeletedRange[],
  insertedText: string,
  author: string,
): void {
  const { state } = view;
  const delType = state.schema.marks.deletion;
  const insType = state.schema.marks.insertion;
  if (!delType) return;

  const date = new Date().toISOString();
  const tr = state.tr;

  // Od konce dokumentu, aby mapování pozic zůstalo platné.
  const sorted = [...deletions].sort((a, b) => b.from - a.from);

  for (const range of sorted) {
    // Označ text jako smazaný (nemazat!).
    state.doc.nodesBetween(range.from, range.to, (node, pos) => {
      if (!node.isText) return;
      const a = Math.max(pos, range.from);
      const b = Math.min(pos + node.nodeSize, range.to);
      if (b <= a) return;
      if (node.marks.some((m) => m.type === delType)) return; // už označené
      tr.addMark(a, b, delType.create({ author, date }));
    });
  }

  // U přepsání vlož nový text za konec označeného úseku a rovnou ho označ
  // jako vložený — vzniknou tak dvě změny (smazání + vložení), přesně jako
  // to dělá Word.
  if (insertedText) {
    const last = sorted[0]; // nejzazší rozsah
    const at = tr.mapping.map(last.to);
    tr.insertText(insertedText, at);
    if (insType) {
      tr.addMark(at, at + insertedText.length, insType.create({ author, date }));
    }
    tr.setSelection(Selection.near(tr.doc.resolve(at + insertedText.length)));
  } else {
    // Čisté mazání — kurzor za označený úsek.
    const at = tr.mapping.map(sorted[0].to);
    tr.setSelection(Selection.near(tr.doc.resolve(at)));
  }

  tr.setMeta(trackChangesKey, true);
  view.dispatch(tr);
}
